import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import type { HeaderHighlightProps, HeaderHighlightRef } from './HeaderHighlight.data';
import styles from './HeaderHighlight.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './HeaderHighlight.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import { NODE_HEADING, MARK_ITALIC, render } from 'storyblok-rich-text-react-renderer';
import Heading from '../../atoms/heading/Heading';
import type { HeadingAs, HeadingSize } from '../../atoms/heading/Heading.data';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';

export default function HeaderHighlight({
  isVisible,
  className,
  blok,
  ref,
}: HeaderHighlightProps & { ref?: Ref<HeaderHighlightRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.headerHighlight, className)}
      {...sbEditable(blok)}
    >
      {render(blok.title, {
        nodeResolvers: {
          [NODE_HEADING]: (children, { level }) => (
            <Heading
              as={`h${level}` as HeadingAs}
              className={classNames(styles[`heading-0${level}`], styles.title)}
              size={`heading-0${level}` as HeadingSize}
              disableTriggerInOnScroll={false}
              enableChineseTransitionFallback
            >
              {children}
            </Heading>
          ),
        },
        markResolvers: {
          [MARK_ITALIC]: (children) => {
            let array = [];
            if (Array.isArray(children)) {
              array = children;
            } else if (typeof children === 'string') {
              array = children.split(' ');
            } else {
              return <>{children}</>;
            }
            return (
              <>
                {array.map((key, index) => (
                  <span key={key} className={styles.highlight}>
                    {key}
                    {index !== array.length - 1 ? ' ' : ''}
                  </span>
                ))}
              </>
            );
          },
        },
      })}

      {blok.button?.[0] && (
        <PrimaryButton
          className={classNames(styles.button)}
          link={{
            href: blok?.button[0]?.link?.cached_url,
            uuid: blok?.button[0]?.link?.id,
          }}
          label={blok.button[0].label}
          disableTriggerInOnScroll={false}
        />
      )}
    </div>
  );
}
